import debounce from 'lodash.debounce';
import { PureComponent } from 'react';
import type React from 'react';
import styled, { css } from 'styled-components';

const dollarSign = css`
  &::before {
    content: '$';
    position: absolute;
    font-size: 18px;
    font-weight: normal;
    color: ${props => props.theme.carvana.blue.dark};
    width: 18px;
    height: 100%;
    left: 7px;
    top: 0;
    padding-top: 2px;
  }
`;
const InputWrapper = styled.div`
  display: inline-block;
  position: relative;
  ${props => props.withDollarSign && dollarSign}
`;

const Input = styled.input`
  /* width: ${props => `${props.width}px`}; */
  width: 88px;
  height: 32px;
  font-size: 16px;
  line-height: 24px;
  text-align: right;
  box-sizing: border-box;
  border-radius: 3px;
  background-color: ${props => props.theme.carvana.white.primary};
  border: 1px solid
    ${props =>
      props.error
        ? props.theme.carvana.red.primary
        : props.theme.carvana.gray.light};
  padding-right: 4px;
  color: ${props =>
    props.error
      ? props.theme.carvana.red.primary
      : props.theme.carvana.blue.primary};
  padding-left: ${props => props.withDollarSign && '12px'};
`;

export type OwnProps = {
  dataQa?: string;
  delay?: number;
  disabled?: boolean;
  error?: boolean;
  focusEventType?: any;
  id?: string;
  length?: number;
  maxLength?: number;
  minLength?: number;
  onChange: (ev: React.BaseSyntheticEvent) => void;
  onFocus?: (focusEventType: any) => void;
  pattern?: string;
  readOnly?: boolean;
  triggerOnEnterKeyDown?: boolean;
  triggerOnTextChange?: boolean;
  type?: string;
  value?: number | string;
  width?: number;
  withDollarSign?: boolean;
};

type OwnState = {
  text: string;
};

const toValueString = (value: number | string | undefined): string =>
  typeof value === 'undefined' ? '' : String(value);

export default class DebouncedInput extends PureComponent<OwnProps, OwnState> {
  static defaultProps = {
    dataQa: undefined,
    delay: 2500,
    disabled: false,
    error: false,
    length: 1,
    maxLength: undefined,
    minLength: undefined,
    pattern: undefined,
    readOnly: false,
    triggerOnEnterKeyDown: true,
    triggerOnTextChange: true,
    type: 'text',
    width: 44,
    withDollarSign: false
  };

  constructor(props: OwnProps) {
    super(props);
    this.onChange = debounce(this.props.onChange, this.props.delay);

    this.state = {
      text: toValueString(this.props.value)
    };
  }

  componentDidMount() {
    this.updateState(this.props.value);
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    this.updateState(nextProps.value);
  }

  onChange: ReturnType<typeof debounce>;

  onBlur: React.FocusEventHandler<HTMLInputElement> = e => {
    e.persist();
    const { length = 1, minLength, value } = this.props;
    if (e.target.value.length >= (minLength || length)) {
      this.onChange.cancel();
      this.updateState(e.target.value);

      const shouldUpdateValue =
        (typeof value === 'number' && value !== Number(e.target.value)) ||
        (typeof value !== 'number' && value !== e.target.value);

      if (shouldUpdateValue) {
        this.props.onChange(e);
      }
    }
  };

  onEnterKeyDown: React.KeyboardEventHandler<HTMLInputElement> = ev => {
    ev.persist();
    const { triggerOnEnterKeyDown, minLength, length = 1 } = this.props;
    if (ev.key === 'Enter' && triggerOnEnterKeyDown) {
      this.updateState(ev.currentTarget.value);

      if (ev.currentTarget.value.length >= (minLength || length)) {
        this.onChange.cancel();
        this.props.onChange(ev);
      }
    }
  };

  updateState = (value: number | string | undefined) => {
    this.setState({ text: toValueString(value) });
  };

  handleTextChange = e => {
    e.persist();
    const { length = 1, minLength, triggerOnTextChange } = this.props;
    this.updateState(e.target.value);
    if (e.target.value.length >= (minLength || length) && triggerOnTextChange) {
      this.onChange(e);
    }
  };

  handleFocus = e => {
    e.target.select();
    if (this.props.onFocus !== undefined) {
      this.props.onFocus(this.props.focusEventType);
    }
  };

  render() {
    const {
      dataQa,
      disabled,
      error,
      id,
      length,
      maxLength,
      pattern,
      readOnly,
      type,
      width,
      withDollarSign
    } = this.props;
    const { text } = this.state;

    return (
      <InputWrapper withDollarSign={withDollarSign}>
        <Input
          data-qa={dataQa}
          disabled={readOnly}
          error={error}
          id={id}
          maxLength={maxLength || length}
          onBlur={this.onBlur}
          onChange={this.handleTextChange}
          onFocus={this.handleFocus}
          onKeyDown={this.onEnterKeyDown}
          pattern={pattern}
          readOnly={disabled}
          type={type}
          value={text}
          width={width}
          withDollarSign={withDollarSign}
        />
      </InputWrapper>
    );
  }
}
